/* eslint-disable no-bitwise */
import { Navigate, Route, Routes } from 'react-router-dom';
import { Permission, permission, Resource } from '@pepperhq/auth-client';
import { PrivateLayout } from 'layouts/PrivateLayout';
import * as routes from 'config/routes';
import { RestrictedRoute } from 'lib/RestrictedRoute';
import Analytics from 'pages/analytics';
import AnalyticsDashboardView from 'pages/analytics/dashboards/view';
import AnalyticsExport from 'pages/analytics/export';
import Customers from 'pages/customers';
import CustomersView from 'pages/customers/view';
import { ExportCustomersPage as CustomersExport } from 'pages/customers/export';
import Locations from 'pages/locations';
import LocationTags from 'pages/location-tags';
import LocationView from 'pages/locations/view';
import Marketing from 'pages/marketing';
import MarketingNotifications from 'pages/marketing/notifications';
import MarketingNotificationsView from 'pages/marketing/notifications/view';
import MarketingPerks from 'pages/marketing/perks';
import MarketingVouchers from 'pages/marketing/vouchers';
import MarketingVouchersView from 'pages/marketing/vouchers/view';
import { AdjustmentSchemesPage } from 'src/pages/marketing/adjustment-schemes';
import { AccessManagementPage } from 'pages/merchant-settings/access-management';
import MobileContent from 'pages/mobile-content';
import MobileContentConfirm from 'pages/mobile-content/confirm';
import LayoutConfig from 'pages/mobile-content/layout-config';
import ModuleConfig from 'pages/mobile-content/module-config';
import TemplateSelection from 'pages/mobile-content/template-selection';
import Merchants from 'pages/user/merchants';
import { BrandingSettingsPage } from './settings/branding';
import { GlobalSettingsPage } from './settings/global';
import { LocationsAndFavouritesSettingsPage } from './settings/location';
import { MarketingSettingsPage } from './settings/marketing';
import { OrderingSettingsPage } from './settings/ordering';
import { PaymentsSettingsPage } from './settings/payments';
import { PointOfSaleSettingsPage } from './settings/pos';
import { IntegrationsSettingsPage } from './settings/integrations';
import { MenuPage } from './menu';
import { OverwritesPage } from './overwrites';
import { SettingsPage } from './settings';
import { RedirectToNewMenu } from 'components/menu/RedirectToNewMenu';
import { TransactionsPage } from './pepper-pay/transactions';
import { ViewTransactionPage } from './pepper-pay/view-transaction';
import { AvailabilityView } from './locations/content/availability/view';
import { ReportingPage } from './pepper-pay/reports/reports';
import { DisputesPage } from './pepper-pay/disputes';
import { ViewDisputePage } from './pepper-pay/view-dispute';
import { BlockList } from './pepper-pay/block-list';
import AnalyticsOrdersPage from './analytics/orders';
import AnalyticsLoyaltyPage from './analytics/loyalty';
import AnalyticsCustomersPage from './analytics/customers';
import { MenuManager } from './menu/MenuManager';
import { MenuScheduling } from './menu/MenuScheduling';
import { TerminalAccessPage } from './terminal';
import { ClerksPage } from './merchant-settings/clerks';
import { GiftingActivityPage } from './analytics/gifting/activity';
import { GiftingLiabilityPage } from 'pages/analytics/gifting/liability';
import { OrdersPage } from './orders';
import { OrderViewPage } from './orders/view';
import { TerminalSettingsPage } from './settings/terminal';
import { TieredLoyalty } from './schemes/tiered-loyalty';
import { OrderingAvailabilityPage } from './locations/content/availability';
import { BirthdayLoyalty } from './schemes/birthday-loyalty';
import { AudiencesPage } from './marketing/audiences';
import Campaigns from 'pages/campaigns';
import { ScheduledCampaignsPage } from 'pages/campaigns/scheduled';
import { ScheduledCampaignViewPage } from 'pages/campaigns/scheduled/view';
import { TriggeredCampaignsPage } from 'pages/campaigns/triggered';
import { TriggeredCampaignViewPage } from 'pages/campaigns/triggered/view';
import { AudienceAnalytics } from './analytics/audience-analytics';
import { MarketingAudiencesView } from './marketing/audiences/view';
import { AutomationsPage } from './automations';
import { ReviewsPage } from './marketing/reviews';
import { TrackingLinksPage } from './campaigns/tracking-links';
import { TrackingLinksViewPage } from './campaigns/tracking-links/view';

const r = (resource: ReturnType<typeof permission>, Component: React.ComponentType) => (
    <RestrictedRoute resources={resource}>
        <Component />
    </RestrictedRoute>
);

export default () => (
    <PrivateLayout>
        <Routes>
            <Route
                path={routes.USER_MERCHANTS}
                element={r(permission(Resource.User, Permission.read), Merchants)}
            />
            {/* Mobile content */}
            <Route
                path={routes.MOBILE_CONTENT}
                element={r(permission(Resource.Layout, Permission.read | Permission.write), MobileContent)}
            />
            <Route
                path={routes.MOBILE_CONTENT_LAYOUT_CONFIG}
                element={r(permission(Resource.Layout, Permission.write), LayoutConfig)}
            />
            <Route
                path={routes.MOBILE_CONTENT_MODULE_CONFIGURATION}
                element={r(permission(Resource.Layout, Permission.write), ModuleConfig)}
            />
            <Route
                path={routes.MOBILE_CONTENT_TEMPLATE_SELECTION}
                element={r(permission(Resource.Layout, Permission.read), TemplateSelection)}
            />
            <Route
                path={routes.MOBILE_CONTENT_CONFIRMATION}
                element={r(permission(Resource.Layout, Permission.write), MobileContentConfirm)}
            />
            {/* Location */}
            <Route
                path={routes.LOCATIONS}
                element={r(permission(Resource.Location, Permission.read), Locations)}
            />
            <Route
                path={routes.LOCATION_VIEW}
                element={r(permission(Resource.Location, Permission.read), LocationView)}
            />
            <Route
                path={routes.LOCATION_TAGS}
                element={r(permission(Resource.Location, Permission.read), LocationTags)}
            />
            <Route
                path={routes.CONTENT_AVAILABILITY}
                element={r(permission(Resource.Menu, Permission.read), OrderingAvailabilityPage)}
            />
            <Route
                path={routes.CONTENT_AVAILABILITY_VIEW}
                element={r(permission(Resource.Menu, Permission.read), AvailabilityView)}
            />
            <Route
                path={routes.MENU_MANAGER_ENRICHMENTS}
                element={r(permission(Resource.Menu, Permission.read), MenuPage)}
            />
            <Route
                path={routes.MENU_MANAGER_ENRICHMENTS_EMPTY}
                element={r(permission(Resource.Menu, Permission.read), RedirectToNewMenu)}
            />
            {/* Analytics */}
            <Route path={routes.ANALYTICS} element={<Analytics />} />
            <Route
                path={routes.ANALYTICS_DASHBOARD_VIEW}
                element={r(permission(Resource.Analytics, Permission.read), AnalyticsDashboardView)}
            />
            <Route
                path={routes.ANALYTICS_ORDERS}
                element={r(permission(Resource.Analytics, Permission.read), AnalyticsOrdersPage)}
            />
            <Route
                path={routes.ANALYTICS_LOYALTY}
                element={r(permission(Resource.Analytics, Permission.read), AnalyticsLoyaltyPage)}
            />
            <Route
                path={routes.ANALYTICS_CUSTOMERS}
                element={r(permission(Resource.Analytics, Permission.read), AnalyticsCustomersPage)}
            />
            <Route
                path={routes.ANALYTICS_EXPORT}
                element={r(permission(Resource.Analytics, Permission.read), AnalyticsExport)}
            />
            {/* Gifting Analytics */}
            <Route
                path={routes.ANALYTICS_GIFTING_ACTIVITY}
                element={r(permission(Resource.Analytics, Permission.read), GiftingActivityPage)}
            />
            <Route
                path={routes.ANALYTICS_GIFTING_LIABILITY}
                element={r(permission(Resource.Analytics, Permission.read), GiftingLiabilityPage)}
            />
            {/* Customers */}
            <Route
                path={routes.CUSTOMERS}
                element={r(permission(Resource.Customer, Permission.read), Customers)}
            />
            <Route
                path={routes.CUSTOMERS_EXPORT}
                element={r(permission(Resource.Customer, Permission.read), CustomersExport)}
            />
            <Route
                path={routes.CUSTOMERS_VIEW}
                element={r(permission(Resource.Customer, Permission.read), CustomersView)}
            />
            {/* Marketing */}
            <Route path={routes.MARKETING} element={<Marketing />} />
            <Route
                path={routes.MARKETING_AUDIENCES}
                element={r(permission(Resource.Segment, Permission.read), AudiencesPage)}
            />
            <Route
                path={routes.MARKETING_AUDIENCES_VIEW}
                element={r(permission(Resource.Segment, Permission.read), MarketingAudiencesView)}
            />
            <Route
                path={routes.MARKETING_NOTIFICATIONS}
                element={r(permission(Resource.Message, Permission.read), MarketingNotifications)}
            />
            <Route
                path={routes.MARKETING_NOTIFICATIONS_VIEW}
                element={r(permission(Resource.Message, Permission.read), MarketingNotificationsView)}
            />
            <Route
                path={`${routes.MARKETING_PERKS}/:type`}
                element={r(permission(Resource.Perk, Permission.read), MarketingPerks)}
            />
            <Route
                path={routes.MARKETING_VOUCHERS}
                element={r(permission(Resource.Voucher, Permission.read), MarketingVouchers)}
            />
            <Route
                path={routes.MARKETING_VOUCHERS_VIEW}
                element={r(permission(Resource.Voucher, Permission.read), MarketingVouchersView)}
            />
            <Route
                path={routes.MARKETING_ADJUSTMENT_SCHEMES}
                element={r(permission(Resource.AdjustmentSchemes, Permission.read), AdjustmentSchemesPage)}
            />
            <Route
                path={routes.MARKETING_REVIEWS}
                element={r(permission(Resource.Reviews, Permission.read), ReviewsPage)}
            />
            {/* Campaigns */}
            <Route
                path={routes.CAMPAIGNS}
                element={r(permission(Resource.Message, Permission.read), Campaigns)}
            />
            <Route
                path={routes.CAMPAIGNS_SCHEDULED}
                element={r(permission(Resource.Message, Permission.read), ScheduledCampaignsPage)}
            />
            <Route
                path={routes.CAMPAIGNS_SCHEDULED_VIEW}
                element={r(permission(Resource.Message, Permission.read), ScheduledCampaignViewPage)}
            />
            <Route
                path={routes.CAMPAIGNS_TRIGGERED}
                element={r(permission(Resource.Message, Permission.read), TriggeredCampaignsPage)}
            />
            <Route
                path={routes.CAMPAIGNS_TRIGGERED_VIEW}
                element={r(permission(Resource.Message, Permission.read), TriggeredCampaignViewPage)}
            />
            <Route
                path={routes.CAMPAIGNS_TRACKING_LINKS}
                element={r(permission(Resource.Links, Permission.read), TrackingLinksPage)}
            />
            <Route
                path={routes.CAMPAIGNS_TRACKING_LINKS_VIEW}
                element={r(permission(Resource.Links, Permission.read), TrackingLinksViewPage)}
            />
            {/* Merchant Settings */}
            <Route
                path={routes.MERCHANT_CONFIG}
                element={r(permission(Resource.Tenant, Permission.write), AccessManagementPage)}
            />
            <Route
                path={routes.MERCHANT_CONFIG_EMPTY}
                element={<Navigate to={routes.MERCHANT_CONFIG_USER} replace />}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_CLERKS}
                element={r(
                    permission(
                        Resource.TerminalAccess,
                        Permission.read | Permission.write | Permission.delete
                    ),
                    ClerksPage
                )}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_GLOBAL}
                element={r(permission(Resource.Setting, Permission.write), GlobalSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_BRANDING}
                element={r(permission(Resource.Setting, Permission.write), BrandingSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_ORDERING}
                element={r(permission(Resource.Setting, Permission.write), OrderingSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_PAYMENTS}
                element={r(permission(Resource.Setting, Permission.write), PaymentsSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_MARKETING}
                element={r(permission(Resource.Setting, Permission.write), MarketingSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_LOCATIONS_AND_FAVOURITES}
                element={r(
                    permission(Resource.Setting, Permission.write),
                    LocationsAndFavouritesSettingsPage
                )}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_POS}
                element={r(permission(Resource.Setting, Permission.write), PointOfSaleSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_TERMINAL}
                element={r(permission(Resource.Setting, Permission.write), TerminalSettingsPage)}
            />
            <Route
                path={routes.MERCHANT_SETTINGS_INTEGRATIONS}
                element={r(
                    permission(Resource.ShopifyPrivateSetting, Permission.write),
                    IntegrationsSettingsPage
                )}
            />
            <Route
                path={routes.MERCHANT_MOBILE_SETTINGS}
                element={r(permission(Resource.Setting, Permission.write), SettingsPage)}
            />
            <Route
                path={routes.ORDERS}
                element={r(permission(Resource.Order, Permission.read), OrdersPage)}
            />
            <Route
                path={routes.ORDERS_VIEW}
                element={r(permission(Resource.Order, Permission.read), OrderViewPage)}
            />
            <Route
                path={routes.CONTENT_OVERWRITES}
                element={r(permission(Resource.Menu, Permission.all), OverwritesPage)}
            />
            <Route
                path={routes.MENU_MANAGER}
                element={r(permission(Resource.Menu, Permission.read), MenuManager)}
            />
            <Route
                path={routes.MENU_MANAGER_SCHEDULING}
                element={r(permission(Resource.Menu, Permission.read), MenuScheduling)}
            />
            {/* Pepper Pay */}
            <Route
                path={routes.PEPPER_PAY_TRANSACTIONS}
                element={r(permission(Resource.Pay, Permission.read), TransactionsPage)}
            />
            <Route
                path={routes.PEPPER_PAY_REPORTING}
                element={r(permission(Resource.PayReporting, Permission.read), ReportingPage)}
            />
            <Route
                path={routes.PEPPER_PAY_REPORTING_EMPTY}
                element={<Navigate to={routes.PEPPER_PAY_REPORTING_REPORTS} replace />}
            />
            <Route
                path={routes.PEPPER_PAY_BLOCK_LIST}
                element={r(permission(Resource.Pay, Permission.read), BlockList)}
            />
            <Route
                path={routes.PEPPER_PAY_TRANSACTIONS_VIEW}
                element={r(permission(Resource.Pay, Permission.read), ViewTransactionPage)}
            />
            <Route
                path={routes.PEPPER_PAY_DISPUTES}
                element={r(permission(Resource.Pay, Permission.read), DisputesPage)}
            />
            <Route
                path={routes.PEPPER_PAY_DISPUTES_VIEW}
                element={r(permission(Resource.Pay, Permission.read), ViewDisputePage)}
            />
            <Route
                path={routes.TERMINAL}
                element={r(permission(Resource.TerminalAccess, Permission.read), TerminalAccessPage)}
            />
            <Route
                path={routes.TIERED_LOYALTY}
                element={r(permission(Resource.TieredLoyaltySchemes, Permission.read), TieredLoyalty)}
            />
            <Route
                path={routes.BIRTHDAY_LOYALTY}
                element={r(permission(Resource.BirthdayLoyaltySchemes, Permission.read), BirthdayLoyalty)}
            />
            <Route
                path={routes.ANALYTICS_AUDIENCES}
                element={r(permission(Resource.Analytics, Permission.read), AudienceAnalytics)}
            />
            <Route
                path={routes.AUTOMATIONS}
                element={r(permission(Resource.Analytics, Permission.read), AutomationsPage)}
            />
            {/* Fallback */}
            <Route path="/" element={<Navigate to={routes.USER_MERCHANTS} replace />} />
        </Routes>
    </PrivateLayout>
);
